import { Toaster } from '@/components/ui/toast';
import { TooltipProvider } from '@/components/ui/tooltip';
import { onHttpError } from '@/utils/http.js';
import { Ziggy } from '@/ziggy.js';
import { createInertiaApp, router } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';

import './echo.js';
import './sentry.js';

// Admin routes aren't in the bundled Ziggy list; the server sends them to admins as a prop.
// beforeUpdate runs ahead of each render, so admin pages can resolve their routes.
const mergeAdminRoutes = (page) => Object.assign(Ziggy.routes, page.props.adminRoutes ?? {});
router.on('beforeUpdate', (event) => mergeAdminRoutes(event.detail.page));

// Stale CSS/asset after deployment — reload to fetch fresh assets
window.addEventListener('vite:preloadError', () => {
    window.location.reload();
});

const toastNetworkError = () => {
    import('@/utils/helpers.jsx').then(({ fireToast }) => {
        fireToast('Network Error', 'Please try again.', 'network-error');
    });
};

// Our own API calls (utils/http.js).
onHttpError((error) => {
    if (error.code === 'ERR_NETWORK') toastNetworkError();
});

// Inertia visits use Inertia's own client, not utils/http.js. Its network errors share the ERR_NETWORK code.
// Don't preventDefault: cancelling the event also skips Inertia's prefetch cleanup, so a hover prefetch
// that failed left its link dead until a full reload. Sentry drops the rethrown HttpNetworkError instead.
router.on('networkError', (event) => {
    if (event.detail.error?.code === 'ERR_NETWORK') toastNetworkError();
});

const domNode = document.getElementById('loader');
const appName = 'Webfluential';

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: async (name) => {
        try {
            const page = await resolvePageComponent(`./Pages/${name}.jsx`, import.meta.glob('./Pages/**/*.jsx'));
            if (!page?.default) {
                window.location.reload();
                return new Promise(() => {});
            }
            if (import.meta.env.DEV && import.meta.env.VITE_WORST_CASE_DATA === 'true') {
                // Worst-case data toggle (?data=worst|empty|one): dev server only, opt in with VITE_WORST_CASE_DATA=true.
                // Production builds drop this branch, so the fixtures never ship.
                const { wrapPage } = await import('./dev/worstCase/withWorstCase.jsx');
                return wrapPage(name, page);
            }
            return page;
        } catch {
            // Stale chunk after deployment — reload to fetch fresh assets
            window.location.reload();
            return new Promise(() => {});
        }
    },
    setup({ el, App, props }) {
        mergeAdminRoutes(props.initialPage);
        createRoot(el).render(
            <TooltipProvider>
                {/* Before App: its effects subscribe first, so a flash toast added on the first render isn't lost. */}
                <Toaster />
                <App {...props} />
            </TooltipProvider>,
        );
    },
    progress: {
        // color: '#80C3C1',
        color: '#fde68a',
        delay: 10,
        includeCSS: true,
        showSpinner: true,
    },
}).then(() => {
    // await new Promise(resolve => setTimeout(resolve, 1000));
    domNode?.remove();
});
